<template>
  <SoWidget no-padding>
    <SoSkeleton
      v-if="
        loading ||
        MeterpointService.getMeterpointTariffs(
          meterpoint.identifier,
          current.coreId.data,
        ).isFetching
      "
      type="card"
      class="ma-2"
    />
    <div v-else class="ma-4">
      <p class="font-weight-medium">
        {{ meterpoint.isElectricity ? "Electricity" : "Gas" }} ({{
          meterpoint.meterpointType
        }}
        - {{ meterpoint.identifier }})
      </p>

      <MeterpointUsageCostHeader :meterpoint="meterpoint" />

      <SoLink
        text="See Calculation"
        :icon="showDetails ? 'chevron-up sm' : 'chevron-down sm'"
        @click="showDetails = !showDetails"
      />
    </div>
    <template #expand>
      <div v-show="showDetails" class="mx-4 expanded-details">
        <SoChip
          v-if="renewals.showRateChangeBanner && tariff?.isVariable"
          data-testid="current-unit-rate-change-chip"
          v-bind="rateChangeChipProps"
          class="ml-1"
        />
        <br />
        {{ isEstimated ? "Estimated" : "Industry" }}
        {{ meterpoint.isElectricity ? "EAC" : "AQ" }}
        {{ meterpoint.totalUsage }} kWh x {{ tariff?.unitRatesFormatted }}<br />
        + {{ DAYS_YEAR }} x {{ standingChargeFormatted }}<br />
        = {{ formatMoney({ amount: annualCost }) }}/year<br />
        /12 = {{ formatMoney({ amount: annualCost / 12 }) }}/month
        <div class="mb-4" />
      </div>
    </template>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useRenewalsStore } from "@/stores/renewals"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { MeterpointService } from "@/services/MeterpointService"
  import { formatMoney, formatPriceInPence } from "@/helpers/moneyHelpers"
  import { rateChangeChipProps } from "@/helpers/unitRateChangeHelper"
  import { DAYS_YEAR } from "@/constants/general"
  import tariffHelper from "@/helpers/tariffHelper"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import MeterpointUsageCostHeader from "./MeterpointUsageCostHeader.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoLink from "@/components/SoLink.vue"

  export default defineComponent({
    components: {
      MeterpointUsageCostHeader,
      SoSkeleton,
      SoWidget,
      SoChip,
      SoLink,
    },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
      isEstimated: {
        type: Boolean,
        default: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        renewals: useRenewalsStore(),
        current: useCurrentAccountStore(),
        MeterpointService,
        showDetails: false,
        rateChangeChipProps,
        formatMoney,
        DAYS_YEAR,
      }
    },
    computed: {
      tariff() {
        return MeterpointService.getMeterpointTariffs(
          this.meterpoint.identifier,
          this.current.coreId.data,
        ).data.currentTariff
      },
      annualCost() {
        return this.tariff
          ? tariffHelper.annualCost(this.tariff, this.meterpoint.registers)
          : 0
      },
      // The backoffice sends the standing charge at full precision, so format
      // rather than interpolating it raw. formatPriceInPence supplies the 'p'.
      standingChargeFormatted() {
        const standingCharge = this.tariff?.productDetails.standingCharge
        return typeof standingCharge === "number"
          ? formatPriceInPence(standingCharge)
          : ""
      },
    },
  })
</script>
